import clsx, { ClassValue } from 'clsx'
import React, { FunctionComponent, ReactNode } from 'react'

import { Theme, useGetSelectedTheme, useThemeClass } from '../Theme'
import { useIsBreakpointMediumAndUp } from '../useBreakpoint'
import styles from './style.module.scss'

export const PanelKinds = {
  Primary: 'Primary',
  Secondary: 'Secondary',
} as const

type PanelKind = (typeof PanelKinds)[keyof typeof PanelKinds]

export const PanelThemeVariantMapping: {
  [K in Theme]?: {
    [T in PanelKind]?: PanelKind
  }
} = {
  [Theme.Cash]: {
    Primary: 'Secondary',
  },
} as const

export interface PanelHeadProps {
  children: ReactNode
}

export interface PanelContentProps {
  bordered?: boolean
  inset?: boolean
  children: ReactNode
}

export interface PanelBaseProps {
  testNameSpace?: string
  kind?: PanelKind
  children: ReactNode
}

export type PanelVariantComponent = FunctionComponent<PanelBaseProps>

export type PanelComponent = PanelVariantComponent & {
  Head: FunctionComponent<PanelHeadProps>
  Content: FunctionComponent<PanelContentProps>
}

const getKindOverride = (selectedTheme: Theme, kind: PanelKind | undefined): PanelKind | undefined => {
  if (kind === undefined) {
    return kind
  }
  return PanelThemeVariantMapping[selectedTheme]?.[kind] ?? kind
}

const BasePanel: FunctionComponent<PanelBaseProps> = ({ children, kind = 'Primary', testNameSpace = '' }) => {
  const themeClass = useThemeClass()
  const selectedTheme = useGetSelectedTheme()
  const isBpMediumUp: boolean | undefined = useIsBreakpointMediumAndUp()
  const kindOverride = getKindOverride(selectedTheme, kind)
  const classNames: ClassValue = clsx(
    styles.panel,
    kindOverride !== undefined ? styles[`panel${kindOverride}`] : '',
    typeof isBpMediumUp !== 'undefined' && !isBpMediumUp && styles.notBpMediumUp,
    styles[`${themeClass}`]
  )
  return (
    <div className={classNames} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-panel` })}>
      {children}
    </div>
  )
}

export const PanelHead: FunctionComponent<PanelHeadProps> = ({ children }) => {
  const themeClass = useThemeClass()
  const classNames: ClassValue = clsx(styles.head, styles[`${themeClass}`])
  return <div className={classNames}>{children}</div>
}

export const PanelContent: FunctionComponent<PanelContentProps> = ({ bordered = false, inset = false, children }) => {
  const classNames: ClassValue = clsx(styles.content, bordered && styles.bordered, inset && styles.inset)
  return <div className={classNames}>{children}</div>
}

export const Panel = BasePanel as PanelComponent

Panel.Head = PanelHead
Panel.Content = PanelContent
